/* eslint-disable react-hooks/exhaustive-deps */
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LoadingPage } from "../../../../common/LoadingPage";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Add, DeleteOutline, EditOutlined, LockResetOutlined } from "@mui/icons-material";
import DataTable from "../../../../common/DataTable";
import { apiRequest } from "../../../../common/common";
import { showNotification } from "../../../../common/Notification/ToastifyNotification";
import { userColumn } from "./ColumnDefs";
import moment from "moment";
import { useSelector } from "react-redux";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Users = ({
    userOpen,
    organizationId,
    isUser,
    setUserOpen,
    getOrganization,
    orgData,
    setOrgData
}) => {
    const { roles } = useSelector(state => state.users);
    const [searchingText, setSearchText] = useState(null);
    const [rowsPerPage, setRowPerPage] = useState(10);
    const [gridApi, setGridApi] = useState(null);
    const [dataLoading, setDataLoding] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [userId, setUserId] = useState(null);
    const [errors, setErrors] = useState({});
    const [roleId, setRoleId] = useState(null);
    const [firstName, setFirstName] = useState(null);
    const [lastName, setLastName] = useState(null);
    const [email, setEmail] = useState(null);
    const [phone, setPhone] = useState(null);
    const [manufacturerData, setManufacturerData] = useState([]);
    const [manufacturerPage, setManufacturerPage] = useState(1);
    const [isManufacturerAvailable, setIsManufacturerAvailable] = useState(true);
    const [manufacturerLoading, setManufacturerLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [suppliers, setSupplier] = useState([]);

    // ── Change Password state ─────────────────────────────────────────────────
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordErrors, setPasswordErrors] = useState({});
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [changePasswordUserId, setChangePasswordUserId] = useState(null);
    // ─────────────────────────────────────────────────────────────────────────

    const getManufacturerData = async (pageNo, searchText) => {
        try {
            setManufacturerLoading(true);
            const response = await apiRequest('get', `/project/list/${organizationId}?&pageNo=${pageNo}&pageSize=10&${searchText ? `searchText=${searchText}` : ''}`);
            const { data } = response.data;
            if (data?.length > 0) {
                setIsManufacturerAvailable(true);
                const array = [...manufacturerData, ...data];
                const uniqueArray = [
                    ...new Set(
                        array.map((item) => JSON.stringify(item)),
                    ),
                ].map((item) => JSON.parse(item));
                setManufacturerData(uniqueArray);
                setManufacturerLoading(false);
            } else {
                setIsManufacturerAvailable(false);
                setManufacturerLoading(false);
            }
        } catch (error) {
            setManufacturerLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    const loadNextManufacturer = async (event) => {
        const listBoxNode = event.currentTarget;
        const numberOfPages = manufacturerPage + 1;
        const position = listBoxNode.scrollTop + listBoxNode.clientHeight;
        if (listBoxNode.scrollHeight - position <= 1 && isManufacturerAvailable) {
            setManufacturerPage(numberOfPages);
            await getManufacturerData(numberOfPages);
        }
    };

    const onPaginationChanged = (event) => {
        if (event.api) {
            const newPageSize = event.api.paginationGetPageSize();
            setRowPerPage(newPageSize);
        }
    };

    const onGridReady = useCallback((params) => {
        if (params?.api) {
            const { api, searchingText } = params;
            setGridApi(api);
            setDataLoding(true);
            const dataSource = {
                rowCount: null,
                getRows: async (params) => {
                    const url = [
                        `/admin/users?organizationId=${organizationId}`,
                        `pageNo=${Math.floor((params.startRow / rowsPerPage) + 1)}`,
                        `pageSize=${rowsPerPage}`,
                        searchingText ? `searchText=${encodeURIComponent(searchingText)}` : ''
                    ]
                        .filter(Boolean)
                        .join('&');
                    try {
                        const response = await apiRequest('get', `${url}`);
                        const { data, total_count } = response.data;
                        let lastRow = -1;
                        if (data?.length <= total_count) {
                            lastRow = total_count;
                        }
                        params.successCallback(data, lastRow);
                    } catch (error) {
                        setDataLoding(false);
                        showNotification(error?.response?.data.error || error.message, 'error');
                    }
                    setDataLoding(false);
                },
            };
            api.setGridOption("datasource", dataSource);
            setTimeout(() => {
                const node = api.getRenderedNodes();
                if (!node?.[0]?.displayed) {
                    api.showNoRowsOverlay();
                }
            }, 5000);
        }
    }, [rowsPerPage, gridApi]);

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            if (organizationId) {
                onGridReady({
                    api: gridApi,
                });
            }
        }
        return () => {
            isMounted = false;
        };
    }, [rowsPerPage, gridApi]);

    // ── Change Password helpers ───────────────────────────────────────────────
    const handleOpenChangePassword = (uid) => {
        setChangePasswordUserId(uid);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordErrors({});
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setChangePasswordOpen(true);
    };

    const handleCloseChangePassword = () => {
        setChangePasswordOpen(false);
        setChangePasswordUserId(null);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordErrors({});
    };

    const handleChangePasswordSave = async () => {
        const errs = {};
        if (!newPassword) {
            errs.newPassword = 'New password is required';
        } else if (newPassword.length < 6) {
            errs.newPassword = 'Password must be at least 6 characters';
        }
        if (!confirmPassword) {
            errs.confirmPassword = 'Please confirm the password';
        } else if (newPassword && newPassword !== confirmPassword) {
            errs.confirmPassword = 'Passwords do not match';
        }
        setPasswordErrors(errs);
        if (Object.keys(errs).length > 0) return;

        try {
            setPasswordLoading(true);
            const response = await apiRequest('post', '/admin/user/reset-password', {
                user_id: changePasswordUserId,
                new_password: newPassword,
            });
            if (response?.data) {
                showNotification(response?.data?.message || 'Password changed successfully', 'success');
                handleCloseChangePassword();
            }
        } catch (error) {
            showNotification(error?.response?.data?.error || error.message, 'error');
        } finally {
            setPasswordLoading(false);
        }
    };
    // ─────────────────────────────────────────────────────────────────────────

    const updatedColumns = useMemo(() => {
        let column = userColumn;
        if (column.length > 0) {
            return column.map((col) => {
                if (col.field === 'created_at') {
                    return {
                        ...col,
                        cellRenderer: (params) => (
                            <span>{params?.data?.created_at ? moment(params?.data?.created_at).format('DD-MM-YYYY') : '--'}</span>
                        ),
                    };
                }
                if (col.field === 'phone_number') {
                    return {
                        ...col,
                        cellRenderer: (params) => (
                            <span>{params?.data?.phone_number || '--'}</span>
                        ),
                    };
                }
                if (col.field === 'is_deleted') {
                    return {
                        ...col,
                        cellRenderer: (params) => (
                            <span>{params?.data?.is_deleted ? 'Block' : params?.data?.is_inactive ? 'Temporary Block' : 'Active'}</span>
                        ),
                    };
                }
                if (col.field === 'role') {
                    return {
                        ...col,
                        cellRenderer: (params) => (
                            <span>{params?.data?.role_id ? roles.find((a) => a.id === params?.data?.role_id)?.name : '--'}</span>
                        ),
                    };
                }
                if (col.field === 'name') {
                    return {
                        ...col,
                        cellRenderer: (params) => (
                            <span>{`${params?.data?.first_name} ${params?.data?.last_name || ''}`}</span>
                        ),
                    };
                }
                if (col.field === 'action') {
                    return {
                        ...col,
                        minWidth: 160,
                        width: 160,
                        cellRenderer: (params) => (
                            <Box style={{ display: 'flex', alignItems: 'center' }}>
                                <Tooltip title="Edit User">
                                    <EditOutlined
                                        color="primary"
                                        style={{ cursor: 'pointer', color: isUser?.code !== 'mvedasMasterAdmin' ? 'gray' : '' }}
                                        onClick={async () => {
                                            if (isUser?.code === 'mvedasMasterAdmin') {
                                                const response = await apiRequest('get', `/users/${params?.data?.user_id}`);
                                                const res = await apiRequest('get', `/admin/user-projects/${params?.data?.user_id}`);
                                                setSupplier(res?.data?.data?.map((a) => a.project_id));
                                                const { data } = response.data;
                                                setFirstName(data?.first_name);
                                                setLastName(data?.last_name);
                                                setEmail(data?.email);
                                                setPhone(data?.phone_number);
                                                setRoleId(data?.role_id);
                                                setDialogOpen(true);
                                                setUserId(params?.data?.user_id);
                                            }
                                        }}
                                    />
                                </Tooltip>
                                {/* ── Change Password icon ── */}
                                <Tooltip title="Change Password">
                                    <LockResetOutlined
                                        style={{
                                            cursor: 'pointer',
                                            color: isUser?.code !== 'mvedasMasterAdmin' ? 'gray' : '#1976d2',
                                            marginLeft: 5,
                                        }}
                                        onClick={() => {
                                            if (isUser?.code === 'mvedasMasterAdmin') {
                                                handleOpenChangePassword(params?.data?.user_id);
                                            }
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title="Delete User">
                                    <DeleteOutline
                                        color="secondary"
                                        style={{ cursor: 'pointer', color: isUser?.code !== 'mvedasMasterAdmin' ? 'gray' : '', marginLeft: 5 }}
                                        onClick={() => {
                                            if (isUser?.code === 'mvedasMasterAdmin') {
                                                setDeleteOpen(true);
                                                setUserId(params?.data?.user_id);
                                            }
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title={params?.data?.is_deleted === false ? 'UnBlock' : 'Block'}>
                                    <Switch
                                        size="small"
                                        disabled={isUser?.code !== 'mvedasMasterAdmin'}
                                        checked={params?.data?.is_deleted === false}
                                        onChange={async (e) => {
                                            setDataLoding(true);
                                            try {
                                                const response = await apiRequest('delete', `/users/${params?.data.user_id}?deleted=${e.target.checked === true ? false : true}`);
                                                if (response?.data) {
                                                    onGridReady({
                                                        api: gridApi
                                                    })
                                                    setDataLoding(false);
                                                    showNotification(response?.data?.message, 'success');
                                                }
                                            } catch (error) {
                                                setDataLoding(false);
                                                showNotification(error?.response?.data.error || error.message, 'error');
                                            }
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                        ),
                    };
                }
                return col;
            })
        }
        return column;
    }, [userColumn, gridApi]);

    useEffect(() => {
        if (organizationId && userOpen) {
            getManufacturerData(manufacturerPage);
        }
    }, [organizationId, userOpen]);

    return (
        <Drawer
            anchor="bottom"
            open={userOpen}
            sx={{
                '& .MuiDrawer-paper': {
                    height: '85%',
                    padding: 2
                }
            }}
        >
            {dataLoading && (<LoadingPage isLoading={dataLoading} />)}

            {/* ── Edit / Create User Dialog ─────────────────────────────────── */}
            <Dialog open={dialogOpen} fullWidth maxWidth="md">
                <DialogTitle>{userId ? 'Edit User' : 'Create User'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={1} mt={0.5}>
                        <Grid item sm={12} md={6}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">First Name <span style={{ color: 'red' }}>*</span></InputLabel>
                                <OutlinedInput
                                    label="First Name"
                                    id="standard-adornment-password"
                                    type='text'
                                    value={firstName || ''}
                                    onChange={(e) => {
                                        const newErrors = errors;
                                        if (e.target.value) {
                                            newErrors.firstName = '';
                                        } else {
                                            newErrors.firstName = 'First Name is required'
                                        }
                                        setErrors(newErrors);
                                        setFirstName(e.target.value)
                                    }}
                                />
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.firstName}</Typography>
                            </FormControl>
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Last Name</InputLabel>
                                <OutlinedInput
                                    label="Last Name"
                                    id="standard-adornment-password"
                                    type='text'
                                    value={lastName || ''}
                                    onChange={(e) => setLastName(e.target.value)}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Email <span style={{ color: 'red' }}>*</span></InputLabel>
                                <OutlinedInput
                                    label="Email"
                                    id="standard-adornment-password"
                                    type='text'
                                    value={email || ''}
                                    onChange={(e) => {
                                        const newErrors = errors;
                                        if (!e.target.value) {
                                            newErrors.email = 'Email is required';
                                        } else if (!/\S+@\S+\.\S+/.test(e.target.value)) {
                                            newErrors.email = 'Email is invalid';
                                        } else {
                                            newErrors.email = '';
                                        }
                                        setErrors(newErrors);
                                        setEmail(e.target.value);
                                    }}
                                />
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.email}</Typography>
                            </FormControl>
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Phone Number</InputLabel>
                                <OutlinedInput
                                    label="Phone Number"
                                    id="standard-adornment-password"
                                    type='number'
                                    value={phone || ''}
                                    onChange={(e) => {
                                        const newErrors = errors;
                                        setPhone(e.target.value)
                                        const phoneRegex = /^(?:\+?(\d{1,2}))?[-.\s]?(\(?\d{3}\)?)[-.\s]?\d{3}[-.\s]?\d{4}$/;
                                        if (phoneRegex.test(e.target.value)) {
                                            newErrors.phoneNumber = '';
                                        } else if (!e.target.value) {
                                            newErrors.phoneNumber = '';
                                        } else {
                                            newErrors.phoneNumber = 'Please provide valid phone number';
                                        }
                                        setErrors(newErrors);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.keyCode === 38 || event.keyCode === 40) {
                                            event.preventDefault();
                                        }
                                    }}
                                />
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.phoneNumber}</Typography>
                            </FormControl>
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="standard-adornment-password">Role <span style={{ color: 'red' }}>*</span></InputLabel>
                                <Select
                                    labelId="demo-simple-select-standard-label"
                                    id="demo-simple-select-standard"
                                    label="Role"
                                    value={roleId || ''}
                                    onChange={(e) => {
                                        const newErrors = errors;
                                        if (e.target.value) {
                                            newErrors.role = '';
                                        } else {
                                            newErrors.role = 'Please Select Role'
                                        }
                                        setErrors(newErrors);
                                        setRoleId(e.target.value);
                                    }}
                                >
                                    {roles.map((a) => (
                                        <MenuItem value={a.id}>{a.name}</MenuItem>
                                    ))}
                                </Select>
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.role}</Typography>
                            </FormControl>
                        </Grid>
                        {roleId === 5 && (
                            <Grid item sm={12} md={6}>
                                <Autocomplete
                                    value={manufacturerData.filter((c) => suppliers?.includes(c.project_id)) || []}
                                    id="checkboxes-tags-demo"
                                    size="small"
                                    fullWidth
                                    multiple
                                    clearIcon={true}
                                    disableCloseOnSelect
                                    options={manufacturerData}
                                    getOptionLabel={(option) => option?.name}
                                    ListboxProps={{
                                        onScroll: (event) => loadNextManufacturer(event),
                                    }}
                                    renderOption={(props, option, { selected }) => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <li key={key} {...optionProps}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                />
                                                {option.name}
                                            </li>
                                        );
                                    }}
                                    onChange={(event, newValue) => {
                                        const newErrors = errors;
                                        if (newValue.length > 0) {
                                            newErrors.suppliers = '';
                                        } else {
                                            newErrors.suppliers = 'Please select a project';
                                        }
                                        setErrors(newErrors);
                                        setSupplier(newValue.map((a) => a.project_id))
                                        setManufacturerPage(1);
                                    }}
                                    inputValue={inputValue === 0 || inputValue === 'on' ? '' : inputValue}
                                    onInputChange={(e) => {
                                        if (e) {
                                            setInputValue(e.target.value);
                                            if (e?.target?.value?.length >= 1) {
                                                setManufacturerPage(1);
                                                getManufacturerData(1, e?.target?.value);
                                            }
                                        }
                                    }}
                                    renderInput={(params) =>
                                        <TextField
                                            fullWidth
                                            {...params}
                                            label="Project *"
                                            name="project"
                                            variant="outlined"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {manufacturerLoading ? <CircularProgress color="primary" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />}
                                />
                                <Typography variant='body2' style={{ color: 'red' }}>{errors.suppliers}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button size="small" variant="contained" color="secondary"
                        onClick={() => {
                            setUserId(null);
                            setDialogOpen(false);
                            setErrors({});
                            setFirstName(null);
                            setLastName(null);
                            setRoleId(null);
                            setEmail(null);
                            setPhone(null);
                            setSupplier([]);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            try {
                                const newErrors = {};
                                if (!firstName) {
                                    newErrors.firstName = 'First Name is required';
                                }
                                if (!email) {
                                    newErrors.email = 'Email is required';
                                }
                                if (!roleId) {
                                    newErrors.role = 'Please Select Role'
                                }

                                if (roleId === 5 && suppliers.length === 0) {
                                    newErrors.suppliers = 'Please Select a Project';
                                }

                                setErrors(newErrors);
                                if (Object.keys(newErrors).length === 0) {
                                    const data = {
                                        "first_name": firstName,
                                        "last_name": lastName,
                                        "email": email,
                                        "phone_number": phone,
                                        role_id: roleId,
                                        organization_id: organizationId
                                    }
                                    if (userId) {
                                        setDataLoding(true);
                                        const response = await apiRequest('put', `/users/${userId}`, data);
                                        if (response?.data) {
                                            setDataLoding(false);
                                            showNotification(response?.data?.message, 'success');
                                            setErrors({});
                                            setFirstName(null);
                                            setLastName(null);
                                            setRoleId(null);
                                            setEmail(null);
                                            setPhone(null);
                                            setDialogOpen(false);
                                            onGridReady({
                                                api: gridApi
                                            });
                                        }
                                        if (suppliers.length > 0 && roleId === 5) {
                                            await apiRequest('post', '/admin/save-user-projects', {
                                                user_id: userId,
                                                organization_id: organizationId,
                                                project_ids: suppliers
                                            });
                                            setSupplier([]);
                                        }
                                    } else {
                                        setDataLoding(true);
                                        const response = await apiRequest('post', `/admin/register`, data);
                                        if (response?.data) {
                                            setDataLoding(false);
                                            showNotification(response?.data?.message, 'success');
                                            if (suppliers.length > 0 && roleId === 5) {
                                                await apiRequest('post', '/admin/save-user-projects', {
                                                    user_id: response?.data?.data?.user_id,
                                                    organization_id: organizationId,
                                                    project_ids: suppliers
                                                });
                                                setSupplier([]);
                                            }
                                            setErrors({});
                                            setFirstName(null);
                                            setLastName(null);
                                            setRoleId(null);
                                            setEmail(null);
                                            setPhone(null);
                                            setDialogOpen(false);
                                            onGridReady({
                                                api: gridApi
                                            });
                                            getOrganization();
                                        }
                                    }
                                }

                            } catch (error) {
                                setDataLoding(false);
                                showNotification(error?.response?.data.error || error.message, 'error');
                            }
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Confirmation Dialog ────────────────────────────────── */}
            <Dialog open={deleteOpen} fullWidth maxWidth="xs">
                <DialogContent>Are you sure to delete this one?</DialogContent>
                <DialogActions>
                    <Button size="small" variant="contained" color="secondary"
                        onClick={() => {
                            setUserId(null);
                            setDeleteOpen(false);
                        }}
                    >
                        No
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            try {
                                setDataLoding(true);
                                const response = await apiRequest('delete', `/admin/user-permanent-delete/${userId}`);
                                if (response?.data) {
                                    setDataLoding(false);
                                    showNotification(response?.data?.message, 'success');
                                    setUserId(null);
                                    setDeleteOpen(false);
                                    onGridReady({
                                        api: gridApi
                                    });
                                }
                            } catch (error) {
                                setDataLoding(false);
                                showNotification(error?.response?.data.error || error.message, 'error');
                            }
                        }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Change Password Dialog ────────────────────────────────────── */}
            <Dialog open={changePasswordOpen} fullWidth maxWidth="xs">
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} mt={0.5}>
                        <Grid item xs={12}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="new-password-input">
                                    New Password <span style={{ color: 'red' }}>*</span>
                                </InputLabel>
                                <OutlinedInput
                                    id="new-password-input"
                                    label="New Password *"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        setPasswordErrors((prev) => ({
                                            ...prev,
                                            newPassword: e.target.value
                                                ? e.target.value.length < 6
                                                    ? 'Password must be at least 6 characters'
                                                    : ''
                                                : 'New password is required',
                                        }));
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowNewPassword((v) => !v)}
                                                edge="end"
                                            >
                                                {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                {passwordErrors.newPassword && (
                                    <Typography variant="body2" style={{ color: 'red' }}>
                                        {passwordErrors.newPassword}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel htmlFor="confirm-password-input">
                                    Confirm Password <span style={{ color: 'red' }}>*</span>
                                </InputLabel>
                                <OutlinedInput
                                    id="confirm-password-input"
                                    label="Confirm Password *"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setPasswordErrors((prev) => ({
                                            ...prev,
                                            confirmPassword: e.target.value
                                                ? newPassword && e.target.value !== newPassword
                                                    ? 'Passwords do not match'
                                                    : ''
                                                : 'Please confirm the password',
                                        }));
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowConfirmPassword((v) => !v)}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                {passwordErrors.confirmPassword && (
                                    <Typography variant="body2" style={{ color: 'red' }}>
                                        {passwordErrors.confirmPassword}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        disabled={passwordLoading}
                        onClick={handleCloseChangePassword}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={passwordLoading}
                        onClick={handleChangePasswordSave}
                        startIcon={passwordLoading ? <CircularProgress size={14} color="inherit" /> : null}
                    >
                        {passwordLoading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Header ───────────────────────────────────────────────────── */}
            <Box display="flex" justifyContent="space-between" mt={1} mb={1}>
                <Box display="flex" alignItems="center">
                    <Tooltip title="Back to organization">
                        <KeyboardArrowLeftIcon style={{ fontSize: 30, cursor: 'pointer' }} color="primary" onClick={() => {
                            setUserOpen(false);
                            setOrgData(null);
                        }} />
                    </Tooltip>
                    <Typography variant='h4' style={{ fontWeight: 'bold' }}>Users</Typography>
                </Box>
                <Box>
                    <TextField
                        size="small"
                        id="outlined-search"
                        type="text"
                        placeholder="Search Users"
                        sx={{
                            background: '#ebeaea',
                            marginRight: 1,
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                searchingText && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => {
                                                setSearchText('');
                                                onGridReady({
                                                    api: gridApi,
                                                    searchingText: ''
                                                });
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            ),
                        }}
                        value={searchingText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onGridReady({
                                    api: gridApi,
                                    searchingText: e.target.value
                                });
                            }
                        }}
                    />
                    {isUser?.code === 'mvedasMasterAdmin' && (
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            startIcon={<Add />}
                            style={{
                                height: 40
                            }}
                            onClick={() => {
                                if (orgData.Subscriptions[0]?.plan_id) {
                                    if (orgData?.['_count']?.user < orgData?.Subscriptions[0]?.user_limit) {
                                        setDialogOpen(true);
                                    } else {
                                        showNotification(`Please note that the user limit has been reached, as assigned by your plan.`, 'error');
                                    }
                                } else {
                                    showNotification(`You do not have an active plan. Please activate a plan to proceed.`, 'error');
                                }
                            }}
                        >
                            Create
                        </Button>
                    )}
                </Box>
            </Box>
            <Divider />
            <DataTable
                height="75vh"
                columnDefs={updatedColumns}
                onGridReady={onGridReady}
                rowsPerPage={rowsPerPage}
                paginationPageSizeSelector={[10, 15, 25, 50, 100]}
                onPaginationChanged={onPaginationChanged}
                modelType="infinite"
            />
        </Drawer>
    )
}

export default Users;